// src/pages/public/MenuPagePublic.jsx
// PAGE PUBLIQUE INDÉPENDANTE — route dédiée, ex: /menu/:slug

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import api from '../../lib/axios'
import MenuRenderer from '../../components/layout/MenuRenderer'
import { normalizeSettings } from '../../lib/normalizeSettings'

export default function MenuPagePublic() {
  const { slug } = useParams()
  const [page, setPage] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [errorInfo, setErrorInfo] = useState('')

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setNotFound(false)
    setErrorInfo('')

    api
      .get(`/public/pages/${slug}`)
      .then(({ data }) => {
        if (cancelled) return
        const p = data?.data ?? data
        if (!p || typeof p !== 'object') {
          setNotFound(true)
          setErrorInfo('Réponse API vide ou invalide.')
          return
        }
        setPage({ ...p, slug: p.slug || slug, settings: normalizeSettings(p.settings) })
      })
      .catch((err) => {
        if (cancelled) return
        console.error('MENU API erreur :', err)
        setErrorInfo(`HTTP ${err.response?.status ?? 'réseau'} — ${err.message}`)
        setNotFound(true)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  // Envoi d'une commande → le serveur recalcule les prix, le client n'envoie que id + quantité.
  const onOrder = useCallback(async (payload) => {
    const { data } = await api.post(`/public/pages/${slug}/orders`, payload)
    return data?.data ?? data // { id, number }
  }, [slug])

  // Suivi du statut (id impossible à deviner : UUID)
  const onTrack = useCallback(async (orderId) => {
    const { data } = await api.get(`/public/orders/${orderId}`)
    return data?.data ?? data // { status }
  }, [])

  // « Appeler serveur » / « Demander l'addition »
  const onServiceRequest = useCallback(async ({ type, table }) => {
    const { data } = await api.post(`/public/pages/${slug}/service-requests`, { type, table })
    return data?.data ?? data
  }, [slug])

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 bg-[#f9f9ff] text-slate-500 text-sm px-6 text-center">
        <p>Ce menu n'existe pas ou n'est plus disponible.</p>
        {import.meta.env.DEV && errorInfo && (
          <p className="text-xs text-red-500">{errorInfo}</p>
        )}
      </div>
    )
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  return <MenuRenderer page={page} onOrder={onOrder} onTrack={onTrack} onServiceRequest={onServiceRequest} />
}